import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import { isoWeek, weekOf } from '../../core/calendar';
import { shortLabels, standardLookup } from '../../core/course';
import { hasMyWeek } from '../../core/home';
import type { AnalyticsEvent, PlannerAction } from '../../core/events';
import { MAX_PLANS } from '../../core/quota';
import { schedulePlan } from '../../core/planbook';
import { overrun as overrunOf, schedule, weekly, type WeekView } from '../../core/schedule';
import type { IsoDate, PlanRecord } from '../../core/types';
import { call, courseLabel, localToday, planPath, toApiError, type ApiError, type GetToken, type PlanResponse, type WeekResponse } from '../api';
import { linkTo, navigate, NEW_PLAN_HREF, planbookHref, planHref, PLANS_HREF, weekHref, type PlanbookAt } from '../navigation';
import { unitColor } from '../unit-color';
import { useNarrow } from '../useNarrow';
import { CurrentUnitPicker, useCurrentUnitAsk } from './CurrentUnitPicker';
import { CombinedCalendar, type CombinedSource } from './PlanCalendar';
import { Planbook } from './Planbook';
import type { RunForPlan, WeekPlan } from './PlanbookWeek';
import { day, plural, type EditRequest } from './UnitRow';
import { WeekSection, WeekStates } from './WeeklyView';

/**
 * My week (R4 to R10): one section per built plan and every plan's units on one calendar. It
 * holds the `/api/week` response as its one state; an edit's answer replaces that plan's record,
 * and the sections and the calendar both derive from it (KTD8). It never loads a plan by its
 * own route, so last opened does not change (R8, KTD2). When `hasMyWeek` no longer offers it,
 * it replaces itself with home (R4, KTD4).
 *
 * With day items on, the combined calendar gives way to the planbook at the route's level, above
 * the sections, and one built plan is enough (R9, KTD5); on a phone `/week` opens this week
 * (R8, KTD4). Every request a plan sends, from its section, its spans, or its items, waits for
 * that plan's last one. With day items on it is the Planbook, the main place to plan, and its
 * sections link to each plan's editor, as the plan's own weekly view is retired.
 */
export function MyWeek({ getToken, myWeek, dayItems = false, collabAttachments = false, at = { zoom: 'year' }, onTrack }: {
  getToken: GetToken; myWeek: boolean; dayItems?: boolean; collabAttachments?: boolean; at?: PlanbookAt;
  onTrack(action: PlannerAction, properties?: AnalyticsEvent['properties']): void;
}) {
  const [data, setData] = useState<WeekResponse | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const trackRef = useRef(onTrack); trackRef.current = onTrack;
  const viewed = useRef(false);
  const container = useRef<HTMLDivElement>(null);
  const { busy, run } = usePlanRequests();
  const narrow = useNarrow();
  const [spanError, setSpanError] = useState<string | null>(null);

  useEffect(() => {
    if (!myWeek) { navigate('/', true); return; }
    let live = true; const controller = new AbortController(); setLoadError(null);
    void call<WeekResponse>(getToken, `/api/week?today=${localToday()}`, { signal: controller.signal })
      .then(response => {
        if (!live) return;
        // Every plan the week returns is built.
        if (!hasMyWeek(response.plans.length, myWeek, dayItems)) { navigate('/', true); return; }
        setData(response);
      })
      .catch(failure => { if (live) setLoadError(toApiError(failure)); });
    return () => { live = false; controller.abort(); };
  }, [getToken, myWeek, dayItems, attempt]);
  const name = dayItems ? 'Planbook' : 'My week';
  useEffect(() => { document.title = `${name} · 240 Planner`; }, [name]);
  // A visit counts as a weekly view of each plan shown, once (R11, KTD5).
  useEffect(() => {
    if (!data || viewed.current) return;
    viewed.current = true;
    for (const p of data.plans) trackRef.current('weekly_viewed', { plan_id: p.id, iso_week: isoWeek(data.today), state: weekly(p.plan, p.plan.calendar, data.today).state, surface: 'my_week' });
  }, [data]);
  // Each built plan with its short label and colour, for both the calendar and its section (KTD6).
  const shownPlans = data?.plans;
  const weekPlans = useMemo((): WeekPlan[] => {
    if (!shownPlans) return [];
    const labels = shortLabels(shownPlans.map(p => p.course));
    return shownPlans.map((record, i) => ({ ...schedulePlan(record), record, label: labels[i]!, colorIndex: i }));
  }, [shownPlans]);
  // Opening /week on a phone lands on this week, and the address says so; a date snaps to its
  // Monday (R7, R8, KTD4). Only the landing: Year chosen later stays Year.
  const [landed, setLanded] = useState(false);
  const shownAt = dayItems && data ? landingAt(at, narrow, landed, data.today) : null;
  const canonical = shownAt && planbookHref(shownAt);
  useEffect(() => {
    if (!canonical) return;
    if (window.location.pathname !== canonical) navigate(canonical, true);
    setLanded(true);
  }, [canonical]);
  const sources = useMemo<CombinedSource[]>(() => weekPlans.map(({ record, scheduled, label, colorIndex }) => ({
    planId: record.id, colorIndex, label, name: courseLabel(record.course), calendar: record.plan.calendar, rows: scheduled,
  })), [weekPlans]);

  if (loadError) return <section className="notice" role="alert">
    <h1>We could not load your week.</h1>
    <p>{loadError.message}</p>
    <div className="page-actions"><a className="secondary button-link" {...linkTo(PLANS_HREF)}>All plans</a><button className="secondary" type="button" onClick={() => setAttempt(n => n + 1)}>Try again</button></div>
  </section>;
  if (!data) return <p className="notice" role="status">Loading your week…</p>;

  const saved = (response: PlanResponse) => setData(d => d && { ...d, plans: d.plans.map(p => (p.id === response.plan.id ? response.plan : p)) });
  /** A span move or reset through the plan's edit path, from My week (R3, KTD3). */
  const moveSpan = async (planId: string, change: EditRequest) => {
    setSpanError(null);
    try {
      const response = await run(planId, () => postEdit(getToken, planId, change));
      if (response) saved(response);
    } catch (failure) {
      setSpanError(`We could not move that standard. ${toApiError(failure).message}`);
    }
  };
  const scrollTo = (planId: string) => {
    const target = Array.from(container.current?.querySelectorAll<HTMLElement>('[data-plan-id]') ?? []).find(el => el.dataset.planId === planId);
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    target?.scrollIntoView?.({ block: 'start', behavior: still ? 'auto' : 'smooth' });
  };

  const sections = <>
    {weekPlans.map(({ record, label, colorIndex }) => <PlanWeek key={record.id} record={record} today={data.today} label={label} planbook={dayItems} colorIndex={colorIndex} getToken={getToken} busy={busy.has(record.id)} run={run} onSaved={saved} />)}
    {data.unbuilt.length > 0 && <Unbuilt plans={data.unbuilt} />}
  </>;
  const heading = <div className="page-heading">
    <div>
      <a className="topic-back" {...linkTo(PLANS_HREF)}>← All plans</a>
      <h1 className="setup-title">{name}</h1>
      {!dayItems && <p className="muted">Week of {day(weekOf(data.today))}</p>}
    </div>
    {data.plans.length + data.unbuilt.length < MAX_PLANS && <div className="page-actions"><a className="secondary button-link" {...linkTo(NEW_PLAN_HREF)}>New plan</a></div>}
  </div>;

  if (shownAt) {
    return <div className="plan-editor my-week my-week-days">
      {heading}
      {spanError && <p className="error-message" role="alert">{spanError}</p>}
      <Planbook plans={weekPlans} today={data.today} at={shownAt} narrow={narrow} getToken={getToken} busy={busy} run={run} collabAttachments={collabAttachments} onMoveSpan={moveSpan} onTrack={onTrack} />
      <div className="my-week-sections" ref={container}>{sections}</div>
    </div>;
  }

  return <div className="plan-editor my-week has-calendar">
    {heading}
    <div className="plan-layout">
      <div className="plan-layout-calendar"><CombinedCalendar sources={sources} today={data.today} onSelectPlan={scrollTo} /></div>
      <div className="plan-layout-main" ref={container}>{sections}</div>
    </div>
  </div>;
}

/** The level shown: on a phone, a first arrival at the Year lands on this week; otherwise the route's (R7, R8, KTD4). */
const landingAt = (at: PlanbookAt, narrow: boolean, landed: boolean, today: IsoDate): PlanbookAt =>
  (narrow && at.zoom === 'year' && !landed ? { zoom: 'week', monday: weekOf(today) } : at);

/**
 * One plan's week (R5, R6): its current unit, this week and next, and its own edits. Beside the
 * planbook it links to the plan's editor, where units are shortened; otherwise to its weekly view.
 */
function PlanWeek({ record, today, label, planbook, colorIndex, getToken, busy, run, onSaved }: {
  record: PlanRecord; today: IsoDate; label: string; planbook: boolean; colorIndex: number; getToken: GetToken; busy: boolean; run: RunForPlan; onSaved(response: PlanResponse): void;
}) {
  const titleId = useId();
  const [error, setError] = useState<string | null>(null);
  const ask = useCurrentUnitAsk();
  const { plan } = record;
  const rows = schedule(plan, plan.calendar);
  const view = weekly(plan, plan.calendar, today, rows);
  const over = overrunOf(plan, plan.calendar);
  const current = rows.find(u => u.id === plan.currentUnitId);
  const textOf = standardLookup(record.standards);
  const standardsOf = (unitId: string) => plan.standards
    .filter(s => s.placement.place === 'unit' && s.placement.unitId === unitId)
    .map(s => textOf(s.id));
  const planLink = planbook ? planHref(record.id) : weekHref(record.id);

  /** One request at a time for this plan, through `usePlanRequests` (KTD8). */
  const edit = async (change: EditRequest): Promise<PlanResponse | null> => {
    setError(null);
    try {
      const response = await run(record.id, () => postEdit(getToken, record.id, change));
      if (response) onSaved(response);
      return response;
    } catch (failure) {
      setError(`We could not save that change. ${toApiError(failure).message}`);
      return null;
    }
  };
  // Units carry the subject's colour here, as its bars do (KTD6).
  const week = (heading: string, w: WeekView | null, shown: ReadonlySet<string> = new Set()) =>
    <WeekSection heading={heading} week={w} currentUnitId={plan.currentUnitId} selectedUnitId={null} indexOf={() => colorIndex} planId={record.id} shown={shown} standardsOf={standardsOf} getToken={getToken} level={3} />;

  return <section className="my-week-plan" aria-labelledby={titleId} data-plan-id={record.id} style={{ '--unit-color': unitColor(colorIndex) } as CSSProperties}>
    <div className="page-heading my-week-plan-heading">
      <div>
        <h2 id={titleId}><span className="cal-swatch" aria-hidden="true" />{courseLabel(record.course)}</h2>
        <p className="muted">{current ? <>Your class is on <strong>{current.name}</strong>.</> : 'No current unit set.'}</p>
        {over.weeksOver > 0 && <p className="unit-flag" role="status">
          The plan runs {plural(over.weeksOver, 'week')} past the last day: {over.units.map(u => `${u.name} (${plural(u.weeksOver, 'week')})`).join(', ')}.{' '}
          <a {...linkTo(planLink)}>Shorten or drop a unit</a>
        </p>}
      </div>
      <div className="page-actions">
        {current && view.state !== 'finished' && <button className="primary" type="button" disabled={busy} onClick={() => void edit({ type: 'still_on_this' })}>Still on this</button>}
        {rows.length > 0 && <button className="secondary" type="button" onClick={ask.open}>{current ? 'Change current unit' : 'Set current unit'}</button>}
        <a className="secondary button-link" {...linkTo(planLink)}>{planbook ? 'Edit' : 'Open'} {label}</a>
      </div>
    </div>
    {error && <p className="error-message" role="alert">{error}</p>}
    <p className="sr-only" role="status">{busy ? 'Saving…' : ''}</p>
    <WeekStates view={view} week={week} />
    {ask.asking(false) && <CurrentUnitPicker units={rows} currentUnitId={plan.currentUnitId} today={today} busy={busy}
      onClose={ask.close}
      onPick={async unitId => { if (await edit({ type: 'anchor', unitId })) ask.close(); }} />}
  </section>;
}

/** An edit through the plan's edit path, named as from My week (KTD7). */
const postEdit = (getToken: GetToken, planId: string, change: EditRequest) =>
  call<PlanResponse>(getToken, `${planPath(planId)}/edits`, { method: 'POST', body: JSON.stringify({ edit: change, from: 'my_week', today: localToday() }) });

/** One request at a time per plan, shared by its section, its spans, and its items (KTD8). */
function usePlanRequests(): { busy: ReadonlySet<string>; run: RunForPlan } {
  const inFlight = useRef(new Set<string>());
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const run = useCallback<RunForPlan>(async (planId, request) => {
    if (inFlight.current.has(planId)) return null;
    inFlight.current.add(planId); setBusy(new Set(inFlight.current));
    try { return await request(); } finally { inFlight.current.delete(planId); setBusy(new Set(inFlight.current)); }
  }, []);
  return { busy, run };
}

/** Plans not built yet, by name, each a link to finish it in the editor (R7). */
function Unbuilt({ plans }: { plans: WeekResponse['unbuilt'] }) {
  const titleId = useId();
  return <section className="my-week-unbuilt" aria-labelledby={titleId}>
    <h2 id={titleId}>Still being built</h2>
    <ul>{plans.map(p => <li key={p.id}><a {...linkTo(planHref(p.id))}>{courseLabel(p.course)}</a></li>)}</ul>
  </section>;
}
